'use client';

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface Feature {
  icon: string;
  label: string;
  desc: string;
}

interface RoleLoginContentProps {
  role: string;
  roleLabel: string;
  redirectPath: string;
  accentColor: 'amber' | 'blue' | 'gold' | 'cyan' | 'violet';
  description: string;
  features: Feature[];
}

const accentStyles: Record<string, { gradient: string; badge: string; btn: string; ring: string }> = {
  amber: {
    gradient: 'from-amber-500/20 via-transparent to-transparent',
    badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    btn: 'bg-amber-500 hover:bg-amber-400 text-black',
    ring: 'focus:ring-amber-500/30',
  },
  blue: {
    gradient: 'from-blue-500/20 via-transparent to-transparent',
    badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    btn: 'bg-blue-500 hover:bg-blue-400 text-white',
    ring: 'focus:ring-blue-500/30',
  },
  gold: {
    gradient: 'from-[#C9A84C]/20 via-transparent to-transparent',
    badge: 'bg-[#C9A84C]/10 text-[#C9A84C] border-[#C9A84C]/20',
    btn: 'bg-[#C9A84C] hover:bg-[#D4B55A] text-black',
    ring: 'focus:ring-[#C9A84C]/30',
  },
  cyan: {
    gradient: 'from-cyan-500/20 via-transparent to-transparent',
    badge: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    btn: 'bg-cyan-500 hover:bg-cyan-400 text-black',
    ring: 'focus:ring-cyan-500/30',
  },
  violet: {
    gradient: 'from-violet-500/20 via-transparent to-transparent',
    badge: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
    btn: 'bg-violet-500 hover:bg-violet-400 text-white',
    ring: 'focus:ring-violet-500/30',
  },
};

export default function RoleLoginContent({ role, roleLabel, redirectPath, accentColor, description, features }: RoleLoginContentProps) {
  const { signIn } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = searchParams.get('next') || redirectPath;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const styles = accentStyles[accentColor];

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signIn(email, password);
      const user = result?.user;
      const userRole = user?.user_metadata?.role || user?.app_metadata?.role || 'customer';

      // Validate role access
      const roleHierarchy: Record<string, number> = { super_admin: 4, admin: 3, merchant: 2, supplier: 2, member: 1, customer: 0 };
      const requiredLevel = roleHierarchy[role] ?? 0;
      const userLevel = roleHierarchy[userRole] ?? 0;

      if (userLevel < requiredLevel) {
        setError(`Access denied. This portal requires ${roleLabel} credentials.`);
        setLoading(false);
        return;
      }

      router.push(next);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e?.message || 'Sign in failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--background)]">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-[48%] relative flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[#0A0F1E] to-[#0D1525]" />
        <div className={`absolute inset-0 opacity-30 bg-gradient-to-br ${styles.gradient}`} />
        <div className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <AppLogo size={32} />
            <span className="font-serif text-lg font-bold text-white">WorldwayLuxe</span>
          </div>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${styles.badge}`}>
            {roleLabel} Portal
          </span>
        </div>
        <div className="relative z-10 space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
              Enterprise<br />Travel Platform
            </h2>
            <p className="text-sm text-white/50">{description}</p>
          </div>
          <div className="space-y-4">
            {features.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg border flex items-center justify-center text-sm shrink-0 ${styles.badge}`}>
                  {f.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{f.label}</p>
                  <p className="text-xs text-white/40">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <p className="text-xs text-white/20">© 2026 WorldwayLuxe. All rights reserved.</p>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <AppLogo size={28} />
            <span className="font-serif text-base font-bold text-foreground">WorldwayLuxe</span>
          </div>

          <div className="mb-8">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border mb-3 ${styles.badge}`}>
              {roleLabel} Access
            </span>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>
              Sign in
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          </div>

          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="you@worldwayluxe.com"
                className={`w-full px-3 py-2.5 text-sm bg-surface-2 border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${styles.ring} transition-all`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className={`w-full px-3 py-2.5 pr-10 text-sm bg-surface-2 border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${styles.ring} transition-all`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={15} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <Icon name="ExclamationTriangleIcon" size={13} className="text-red-400 shrink-0" />
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 ${styles.btn}`}
            >
              {loading ? 'Signing in...' : `Sign in as ${roleLabel}`}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground text-center mb-3">Other portals</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Customer', href: '/customer/login' },
                { label: 'Member', href: '/member/login' },
                { label: 'Partner', href: '/partner/login' },
                { label: 'Admin', href: '/admin/login' },
              ].filter(l => !l.href.includes(`/${role.replace('_', '-')}/`)).slice(0, 4).map(l => (
                <Link key={l.href} href={l.href}
                  className="text-center px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-surface-2 transition-colors">
                  {l.label} Portal
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-4 text-center">
            <Link href="/auth/reset-password" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Forgot password?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
